/* eslint-disable react/no-danger */
import { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { RadioGroup } from '@headlessui/react'
import { useDebouncedInput } from 'hooks/useDebouncedInput'
import Skeleton from 'react-loading-skeleton'
import { trackEvent } from 'analytics/trackFunctions'

// utils + constants
import { toLocaleDate } from 'utils/date-fns'
import {
  CREDIT_TYPES,
  CREDIT_LINE_STATE,
  INTEREST_FREE_ADVANCE_MAX_AMOUNT,
  OFFER_ERROR_SLUGS
} from 'constants/credit'
import { MEMBERSHIP_STATUS } from 'constants/subscriptions'
import {
  hasActiveMembership,
  getMembershipStatus,
  hasSuspendedMembership,
  moneyFormat
} from 'utils'
import { ZENDESK_URL } from 'config'
// types
import { CreditLines } from 'types/api/loan'
import { reversePlans } from 'utils/advance'

// contexts + store
import { MeContext } from 'contexts/me'
import { useCommercialOffer } from 'store/commercialOffer'

// api
import { ADVANCE_TERMS_SLUG, getTerms } from 'api/term-conditions'
import { getOffer, getAdvancePlans } from 'api/advances'

// components
import { Checkbox } from 'components/Checkbox'
import PageWrapper from 'components/PageWrapper'
import PrimaryButton from 'components/primaryButton'
import {
  ProgressBar,
  InstallmentCard,
  AmountInput
} from 'components/AdvanceOffer'
import Tag from 'components/Tag'
import MembershipOptions from 'components/AdvanceOffer/MembershipOptions'
import TextButton from 'components/textButton'
import TermsModal from './TermsModal'

const { membership, noMembership } = CREDIT_LINE_STATE
const { DELAYED } = MEMBERSHIP_STATUS
const { inArrears, badScore, noProposalAvailable, lineIsDepleted } =
  OFFER_ERROR_SLUGS

const RequestAdvance = () => {
  // loading states
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)

  // advance states
  const [creditLine, setCreditLine] = useCommercialOffer((state) => [
    state.creditLine,
    state.setCreditLine
  ])
  const [installments, setInstallments] = useState(null)
  const [chosenInstallment, setChosenInstallment] = useState(null)
  const [inputValue, setInputValue] = useState('')

  // errors
  const [isMembershipMandatoryError, setIsMembershipMandatoryError] =
    useState(false)

  // membership states
  const [membershipSwitch, setMembershipSwitch] = useState(true)

  // modals
  const [isTCModalOpen, setIsTCModalOpen] = useState(false)

  // terms
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [termsContent, setTermsContent] = useState(null)

  // others
  const inputRef = useRef()
  const debouncedValue = useDebouncedInput(inputValue, 200)
  const { me } = useContext(MeContext)
  const navigate = useNavigate()

  const setSelectedOfferStore = useCommercialOffer(
    (state) => state.setSelectedOffer
  )
  const setIsMembershipSelected = useCommercialOffer(
    (state) => state.setIsMembershipSelected
  )

  const plansToShow = membershipSwitch ? membership : noMembership

  useEffect(() => {
    if (isPageLoading) return
    if (membershipSwitch) setIsMembershipMandatoryError(false)
    const index = installments[plansToShow].plans.findIndex(
      (i) => i.id === chosenInstallment.id
    )

    setChosenInstallment(installments[plansToShow].plans[index])
  }, [membershipSwitch])

  useEffect(() => {
    const selectedAmount = Number(debouncedValue) * 1000
    setIsLoadingOptions(true)

    if (
      !debouncedValue ||
      debouncedValue === '' ||
      selectedAmount < creditLine.minAmount ||
      selectedAmount > creditLine.maxAmount
    )
      return

    getAdvancePlans(selectedAmount, 'ADVANCE_ZERO_RATE')
      .then((res) => {
        if (res.success) {
          setInstallments(reversePlans(res.data.result))
          setChosenInstallment(res.data.result[membership].plans[0])
        }
      })
      .finally(() => setIsLoadingOptions(false))
  }, [debouncedValue])

  useEffect(() => {
    trackEvent({ eventName: 'bille_adelanto_calculo_oferta_tasa_0' })

    Promise.all([getOffer(), getTerms(ADVANCE_TERMS_SLUG)])
      .then((res) => {
        const [offer, terms] = res

        setTermsContent(terms.body)

        if (offer.success) {
          const data: CreditLines = offer.data.result
          const { interest_free_advances: interestFree } = data

          trackEvent({
            eventName: 'bille_pantalla_adelantos_oferta_comercial_tasa_0',
            props: {
              monto_linea_credito: interestFree.max_amount
            }
          })

          if (!interestFree.max_amount) {
            navigate('/error-adelanto/error-monto-maximo', { replace: true })
          }

          setCreditLine({
            minAmount: interestFree.min_amount,
            maxAmount: interestFree.max_amount,
            expiresAt: data.expires_at,
            advanceCounter: data.trial_advances_count
          })

          setInstallments(reversePlans(interestFree.max_option))
          setInputValue((interestFree.max_amount / 1000).toString())
          setChosenInstallment(interestFree.max_option[membership].plans[0])
        } else {
          switch (offer.error.code) {
            case inArrears:
              navigate('/error-adelanto/error-prestamo-demorado', {
                replace: true
              })
              break
            case badScore:
            case noProposalAvailable:
              navigate('/error-adelanto/error-sin-oferta', { replace: true })
              break
            case lineIsDepleted:
              navigate('/error-adelanto/error-monto-maximo-tasa-0', {
                replace: true
              })
              break
            default:
              trackEvent({
                eventName: 'bille_adelantos_calculo_error_default_tasa_0',
                props: { api_response: JSON.stringify(offer) }
              })
              navigate('/error-adelanto')
          }
        }
      })
      .catch((error) => {
        trackEvent({
          eventName: 'bille_adelantos_calculo_error_catch_tasa_0',
          props: { api_response: JSON.stringify(error) }
        })
        navigate('/error-adelanto', { replace: true })
      })
      .finally(() => setIsPageLoading(false))
  }, [])

  return (
    <>
      <PageWrapper noTabBar viewSelected='/adelantos' title='Pedir adelanto'>
        <section className='sm:max-w-sm flex flex-col items-center sm:mt-0 mx-auto min-h-[80vh] md:min-h-max'>
          {isPageLoading || me === undefined || me === null ? (
            <div className='w-full'>
              <Skeleton height={21} className='mt-4 mb-8' />
              <Skeleton height={341} />
              <Skeleton height={32} className='mt-8 mb-8' />
              <Skeleton height={48} className='mt-6' />
            </div>
          ) : (
            <>
              <h1 className='font-bold text-xl text-center mb-4'>
                Podemos darte al instante
              </h1>
              <AmountInput
                minAmount={creditLine.minAmount}
                maxAmount={creditLine.maxAmount}
                ref={inputRef}
                value={inputValue}
                setValue={setInputValue}
              />
              <ProgressBar
                minValue={creditLine.minAmount}
                maxValue={creditLine.maxAmount}
                setValue={setInputValue}
                value={Number(inputValue) * 1000}
                className='mt-3'
                inputRef={inputRef}
              />
              <h2 className='font-bold text-xl text-center my-4'>
                ¿Cómo querés devolverlo?
              </h2>
              <Tag className='mx-auto px-8'>
                👌 Comenzá a pagar el{' '}
                {toLocaleDate(installments[membership].start_date)}
              </Tag>
              {isLoadingOptions ? (
                <div className='w-full'>
                  <Skeleton height={67} className='mt-5 mb-1' />
                </div>
              ) : (
                <>
                  <RadioGroup
                    value={chosenInstallment}
                    onChange={setChosenInstallment}
                    className='space-y-2 mt-4 w-full'
                  >
                    {installments[plansToShow].plans.map((i, idx) => (
                      <InstallmentCard
                        originalRate={
                          plansToShow === membership
                            ? installments[noMembership].plans[idx]
                                .interest_rate
                            : null
                        }
                        interestFree
                        idx={idx}
                        key={idx}
                        installment={i}
                      />
                    ))}
                  </RadioGroup>
                  <p className='font-medium text-gray-900 text-2xs mt-2 mb-4'>
                    *<span className='font-bold'>Para más cuotas,</span> tenés
                    que volver atrás y elegir un monto superior a{' '}
                    {moneyFormat(INTEREST_FREE_ADVANCE_MAX_AMOUNT)}.
                  </p>
                </>
              )}

              <MembershipOptions
                isInterestFree
                isMembershipSelected={
                  hasActiveMembership(me?.subscriber_products)
                    ? false
                    : membershipSwitch
                }
                setIsMembershipSelected={setMembershipSwitch}
                suscriptionIsMandatoryError={isMembershipMandatoryError}
                membershipStatus={getMembershipStatus(me?.subscriber_products)}
              />

              <div className='mt-auto'>
                <div className='flex items-center gap-2 mt-4 w-full'>
                  <Checkbox
                    onClick={() => {
                      setAcceptTerms((prevState) => !prevState)
                    }}
                    interactive
                    checked={acceptTerms}
                  />
                  <TextButton
                    handleFunction={() => {
                      setIsTCModalOpen(true)
                    }}
                    className='text-left text-xs leading-tight hover:no-underline'
                  >
                    <span>
                      <span className='font-medium text-black'>Acepto los</span>{' '}
                      <span className='hover:underline'>
                        términos y condiciones del adelanto{' '}
                        {!hasActiveMembership(me?.subscriber_products)
                          ? 'y la membresía'
                          : ''}
                      </span>
                      <span className='font-medium text-black'>y de la</span>{' '}
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          window.open(
                            `${ZENDESK_URL}/articles/28305954448155-Términos-y-condiciones-Promoción-Tasa-0`,
                            '_blank'
                          )
                        }}
                        className='hover:underline'
                      >
                        promoción de adelantos a tasa 0
                      </span>
                    </span>
                  </TextButton>
                </div>

                <PrimaryButton
                  className='mt-6 mb-4'
                  buttonText='Pedir'
                  primaryButtonFunction={() => {
                    if (
                      !membershipSwitch ||
                      hasSuspendedMembership(me?.subscriber_products) ||
                      getMembershipStatus(me?.subscriber_products) === DELAYED
                    ) {
                      setIsMembershipMandatoryError(true)
                    } else {
                      trackEvent({
                        eventName:
                          'bille_adelantos_oferta_comercial_tasa_0_pedir'
                      })
                      setSelectedOfferStore({
                        membership: chosenInstallment,
                        noMembership: installments.no_membership.plans.find(
                          (i) => i.id === chosenInstallment.id
                        ),
                        creditOptionId: installments.membership.id,
                        startDate: installments.membership.start_date,
                        advanceType: CREDIT_TYPES.ZERO_RATE
                      })
                      setIsMembershipSelected(membershipSwitch)
                      navigate('/pedir-adelanto/confirmar')
                    }
                  }}
                  isDisabled={
                    !acceptTerms ||
                    !chosenInstallment ||
                    hasSuspendedMembership(me?.subscriber_products) ||
                    getMembershipStatus(me?.subscriber_products) === DELAYED ||
                    Number(inputValue) * 1000 > creditLine.maxAmount
                  }
                />
              </div>
            </>
          )}
        </section>
      </PageWrapper>

      {/** ************     MODALS      ************** */}

      {!isPageLoading && (
        <TermsModal
          content={termsContent}
          isOpen={isTCModalOpen}
          setIsOpen={setIsTCModalOpen}
        />
      )}
    </>
  )
}

export default RequestAdvance
